import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ApiURL from "../controllers/Api";
import AdminLayout from "./AdminLayout";
import CompanyLayout from "./CompanyLayout";
import {
  Donut, PieChart, HBars, StackedBar, Bubbles, AreaLine,
  VBars, Sparkline, RadialRing, ChartCard, SectionHead,
} from "../components/dashboard/DashboardCharts";

/* ================================================================== */
/*  Design tokens                                                      */
/* ================================================================== */
const PIE_PALETTE = ["#2563EB","#0EA5E9","#8B5CF6","#10B981","#F59E0B","#94A3B8"];

const STATUS_HEX = {
  New:                    "#94A3B8",
  Screening:              "#F59E0B",
  "Interview Scheduled":  "#0EA5E9",
  "Interview Completed":  "#06B6D4",
  Shortlisted:            "#8B5CF6",
  "Offer Released":       "#6366F1",
  Hired:                  "#10B981",
  Closed:                 "#F43F5E",
};

const statusClasses = {
  New:                    "bg-slate-100 text-slate-700",
  Screening:              "bg-amber-100 text-amber-700",
  "Interview Scheduled":  "bg-sky-100 text-sky-700",
  "Interview Completed":  "bg-cyan-100 text-cyan-700",
  Shortlisted:            "bg-violet-100 text-violet-700",
  "Offer Released":       "bg-indigo-100 text-indigo-700",
  Closed:                 "bg-rose-100 text-rose-700",
  Hired:                  "bg-emerald-100 text-emerald-700",
};

const activeStatuses = [
  "New","Screening","Interview Scheduled","Interview Completed","Shortlisted","Offer Released",
];
const allWorkflowStatuses = [
  "New","Screening","Interview Scheduled","Interview Completed","Shortlisted","Offer Released","Hired","Closed",
];

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */
const formatDate = v => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN",{ day:"2-digit",month:"short" });
};

const formatLpa = amount => {
  if (amount == null || amount === "") return "—";
  return `₹${Number(amount).toLocaleString("en-IN",{ minimumFractionDigits:0, maximumFractionDigits:2 })} LPA`;
};

const convertToLpa = (amount, unit) => {
  if (amount == null || amount === "") return null;
  return unit === "per_month" ? Number(amount)*12/100000 : Number(amount);
};

const formatHiredAmount = (amount,unit) => formatLpa(convertToLpa(amount,unit));
const formatAggregateHiredValue = amount => formatLpa(amount);

/* ================================================================== */
/*  Stat card (compact)                                               */
/* ================================================================== */
const StatCard = ({ label, value, helper, accent, trend }) => (
  <motion.div
    initial={{ opacity:0, y:10 }}
    animate={{ opacity:1, y:0 }}
    transition={{ duration:0.35, ease:[0.22,1,0.36,1] }}
    style={{
      background:"#fff",
      borderRadius:10,
      border:"1px solid #E8ECF4",
      padding:"12px 14px",
      boxShadow:"0 1px 2px rgba(0,0,0,0.04)",
      position:"relative",
      overflow:"hidden",
    }}
  >
    {accent && (
      <div style={{
        position:"absolute", top:0, left:0, right:0, height:2,
        background:accent, borderRadius:"10px 10px 0 0",
      }} />
    )}
    <div style={{ fontSize:10.5, fontWeight:600, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.05em", marginTop:accent?4:0 }}>
      {label}
    </div>
    <div style={{ fontSize:24, fontWeight:800, color:"#1E293B", lineHeight:1.1, marginTop:4 }}>
      {value}
    </div>
    {helper && (
      <div style={{ fontSize:10, color:"#94A3B8", marginTop:4, lineHeight:1.4 }}>{helper}</div>
    )}
    {trend && (
      <div style={{ marginTop:6 }}>
        <Sparkline data={trend} color="#3B82F6" height={30} />
      </div>
    )}
  </motion.div>
);

/* ================================================================== */
/*  Mini pipeline funnel                                               */
/* ================================================================== */
const PipelineFunnel = ({ total, active, hired, closed }) => {
  const steps = [
    { label:"Total", value:total, color:"#6366F1" },
    { label:"Active", value:active, color:"#F59E0B" },
    { label:"Hired", value:hired, color:"#10B981" },
    { label:"Closed", value:closed, color:"#F43F5E" },
  ];
  const max = total || 1;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
      {steps.map((s,i) => (
        <div key={s.label}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:11, marginBottom:3 }}>
            <span style={{ color:"#475569", fontWeight:500 }}>{s.label}</span>
            <span style={{ fontWeight:700, color:"#1E293B" }}>{s.value}</span>
          </div>
          <div style={{ height:7, background:"#F1F5F9", borderRadius:99, overflow:"hidden" }}>
            <motion.div
              style={{ height:"100%", background:s.color, borderRadius:99 }}
              initial={{ width:0 }}
              whileInView={{ width:`${(s.value/max)*100}%` }}
              viewport={{ once:true }}
              transition={{ duration:0.7, ease:[0.22,1,0.36,1], delay:0.08*i }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

/* ================================================================== */
/*  Conversion rate ring group                                         */
/* ================================================================== */
const RingGroup = ({ total, hired, active, closed }) => {
  const convRate = total ? Math.round((hired/total)*100) : 0;
  const activeRate = total ? Math.round((active/total)*100) : 0;
  const closedRate = total ? Math.round((closed/total)*100) : 0;

  return (
    <div style={{ display:"flex", justifyContent:"space-around", alignItems:"center", padding:"4px 0" }}>
      <RadialRing value={convRate} max={100} color="#10B981" size={64} label="Hire Rate" />
      <RadialRing value={activeRate} max={100} color="#F59E0B" size={64} label="Active" />
      <RadialRing value={closedRate} max={100} color="#F43F5E" size={64} label="Closed" />
    </div>
  );
};

/* ================================================================== */
/*  Workflow status mini-bar chart                                     */
/* ================================================================== */
const WorkflowStatusBars = ({ topStatuses }) => {
  const max = Math.max(...topStatuses.map(s => s.count), 1);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
      {topStatuses.filter(s => s.count > 0).map(({ status, count }) => (
        <div key={status} style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClasses[status]||"bg-slate-100 text-slate-700"}`}
            style={{ minWidth:90, justifyContent:"center" }}>
            {status}
          </span>
          <div style={{ flex:1, height:6, background:"#F1F5F9", borderRadius:99, overflow:"hidden" }}>
            <motion.div
              style={{ height:"100%", background:STATUS_HEX[status]||"#94A3B8", borderRadius:99 }}
              initial={{ width:0 }}
              whileInView={{ width:`${(count/max)*100}%` }}
              viewport={{ once:true }}
              transition={{ duration:0.6, ease:[0.22,1,0.36,1] }}
            />
          </div>
          <span style={{ fontSize:11, fontWeight:700, color:"#1E293B", minWidth:20, textAlign:"right" }}>{count}</span>
        </div>
      ))}
    </div>
  );
};

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
export default function AssignmentStatus() {
  const isCompanyView = localStorage.getItem("userType") === "company";
  const Layout = isCompanyView ? CompanyLayout : AdminLayout;

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total:0, assigned:0, active:0, hired:0, closed:0 });
  const [recruiterSummary, setRecruiterSummary] = useState({
    total_recruiters:0, active_recruiters:0, total_assignments:0,
    active_pipeline:0, total_hires:0, total_followups:0, total_hired_amount:0,
  });
  const [followupSummary, setFollowupSummary] = useState({ total:0, pending:0, completed:0, callbacks:0 });
  const [subCompanies, setSubCompanies] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedRecruiters, setExpandedRecruiters] = useState({});

  /* ---- data fetching ---- */
  useEffect(() => {
    const loadAssignments = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${ApiURL}crm/admin/get_assignment_status_report.php`, {
          headers: token ? { Authorization:`Bearer ${token}` } : {},
        });
        const result = await res.json().catch(()=>({}));
        if (!res.ok || result.success===false) throw new Error(result.message||"Failed");
        setRows(result?.data?.rows||[]);
        setSummary(result?.data?.summary||{ total:0,assigned:0,active:0,hired:0,closed:0 });
      } catch(e) { console.error(e); }
    };
    loadAssignments();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization:`Bearer ${token}` } : {};
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${ApiURL}get_visible_resumes.php`,{ headers });
        const result = await res.json().catch(()=>({}));
        if (mounted && res.ok && result.success!==false) setResumes(Array.isArray(result.data)?result.data:[]);
      } catch(e) { console.error(e); }
    })();
    return () => { mounted=false; };
  }, []);

  useEffect(() => {
    if (!isCompanyView) return;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization:`Bearer ${token}` } : {};
    let mounted = true;
    (async () => {
      try {
        const [rRes,fRes,sRes] = await Promise.all([
          fetch(`${ApiURL}crm/company/get_recruiter_dashboard.php`,{headers}),
          fetch(`${ApiURL}crm/company/get_followup_report.php`,{headers}),
          fetch(`${ApiURL}crm/company/get_sub_companies.php`,{headers}),
        ]);
        const [rResult,fResult,sResult] = await Promise.all([rRes.json().catch(()=>({})),fRes.json().catch(()=>({})),sRes.json().catch(()=>({}))]);
        if (!mounted) return;
        if (rRes.ok && rResult.success!==false) setRecruiterSummary(rResult?.data?.summary||recruiterSummary);
        if (fRes.ok && fResult.success!==false) setFollowupSummary(fResult?.data?.summary||followupSummary);
        if (sRes.ok && sResult.success!==false) setSubCompanies(sResult?.data?.sub_companies||[]);
      } catch(e) { console.error(e); }
    })();
    return () => { mounted=false; };
  }, [isCompanyView]);

  /* ---- filtered rows ---- */
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(row =>
      [row.original_filename,row.candidate_name,row.candidate_email,row.candidate_role,
       row.recruiter_name,row.sub_company_name,row.requirement_name,row.requirement_notes,
       row.lead_status,row.hired_company_name,row.hired_requirement_name]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(term))
    );
  }, [rows, search]);

  /* ---- recruiter groups ---- */
  const recruiterGroups = useMemo(() => {
    const groups = filteredRows.reduce((acc, row) => {
      const key = String(row.recruiter_id||row.recruiter_personnel_id||row.recruiter_email||row.recruiter_name||`unassigned-${row.id}`);
      if (!acc[key]) acc[key] = {
        recruiterKey:key,
        recruiterName:row.recruiter_name||"Unassigned Recruiter",
        recruiterMeta:row.recruiter_personnel_id||row.recruiter_email||"—",
        rows:[],
      };
      acc[key].rows.push(row);
      return acc;
    }, {});

    return Object.values(groups).map(g => ({
      ...g,
      total:g.rows.length,
      active:g.rows.filter(r => activeStatuses.includes(r.lead_status)).length,
      hired:g.rows.filter(r => r.lead_status==="Hired").length,
      closed:g.rows.filter(r => r.lead_status==="Closed").length,
      latestUpdatedAt:g.rows.reduce((latest,r) => {
        const c=r.updated_at||r.assigned_at;
        if (!c) return latest;
        if (!latest) return c;
        return new Date(c)>new Date(latest)?c:latest;
      },""),
    })).sort((a,b) => {
      const ad = a.latestUpdatedAt?new Date(a.latestUpdatedAt).getTime():0;
      const bd = b.latestUpdatedAt?new Date(b.latestUpdatedAt).getTime():0;
      return bd-ad||a.recruiterName.localeCompare(b.recruiterName);
    });
  }, [filteredRows]);

  useEffect(() => {
    setExpandedRecruiters(prev =>
      recruiterGroups.reduce((next,g) => {
        if (prev[g.recruiterKey]) next[g.recruiterKey]=true;
        return next;
      },{})
    );
  }, [recruiterGroups]);

  /* ---- summary ---- */
  const filteredSummary = useMemo(() => ({
    total:filteredRows.length,
    assigned:filteredRows.length,
    active:filteredRows.filter(r => activeStatuses.includes(r.lead_status)).length,
    hired:filteredRows.filter(r => r.lead_status==="Hired").length,
    closed:filteredRows.filter(r => r.lead_status==="Closed").length,
  }),[filteredRows]);

  const displaySummary = search.trim() ? filteredSummary : summary;
  const topStatuses = allWorkflowStatuses.map(status => ({
    status,
    count:filteredRows.filter(r => r.lead_status===status).length,
  })).sort((a,b) => b.count-a.count||a.status.localeCompare(b.status));

  const topRecruiters = recruiterGroups.slice(0,5);

  /* ---- main analytics ---- */
  const analytics = useMemo(() => {
    const statusMix = topStatuses.filter(s=>s.count>0).map(s => ({
      label:s.status, value:s.count, color:STATUS_HEX[s.status]||"#94A3B8",
    }));
    const outcomes = [
      { label:"Active",  value:displaySummary.active||0,  color:"#F59E0B" },
      { label:"Hired",   value:displaySummary.hired||0,   color:"#10B981" },
      { label:"Closed",  value:displaySummary.closed||0,  color:"#F43F5E" },
    ];
    const followups = [
      { label:"Completed", value:followupSummary.completed||0, color:"#10B981" },
      { label:"Pending",   value:followupSummary.pending||0,   color:"#F59E0B" },
      { label:"Callbacks", value:followupSummary.callbacks||0, color:"#0EA5E9" },
    ];
    const recruiterStacked = topRecruiters.map(g => ({
      label:(g.recruiterName||"N/A").split(" ")[0].slice(0,9),
      segments:[
        { key:"Active", value:g.active, color:"#F59E0B" },
        { key:"Hired",  value:g.hired,  color:"#10B981" },
        { key:"Closed", value:g.closed, color:"#F43F5E" },
      ],
    }));
    const recruiterKeys = [
      { label:"Active", color:"#F59E0B" },
      { label:"Hired",  color:"#10B981" },
      { label:"Closed", color:"#F43F5E" },
    ];
    const subBubbles = subCompanies
      .map(c => ({ label:(c.name||"Client").slice(0,9), value:c.requirements?.length||0 }))
      .filter(b=>b.value>0);
    const bubbleData = subBubbles.length
      ? subBubbles
      : topRecruiters.map(g => ({ label:(g.recruiterName||"N/A").split(" ")[0].slice(0,9), value:g.total }));

    const now = new Date();
    const months = new Map();
    for (let i=5;i>=0;i--) {
      const d = new Date(now.getFullYear(),now.getMonth()-i,1);
      months.set(`${d.getFullYear()}-${d.getMonth()}`,{ label:d.toLocaleString("en-US",{month:"short"}), value:0 });
    }
    rows.forEach(row => {
      const v=row.assigned_at||row.updated_at;
      if (!v) return;
      const d=new Date(v);
      if (isNaN(d.getTime())) return;
      const key=`${d.getFullYear()}-${d.getMonth()}`;
      if (months.has(key)) months.get(key).value++;
    });
    const trend=[...months.values()];

    /* per-recruiter hired amounts */
    const recruiterHiredAmounts = topRecruiters.map(g => {
      const total = g.rows.filter(r=>r.lead_status==="Hired" && r.hired_amount)
        .reduce((s,r) => s + (convertToLpa(r.hired_amount,r.hired_amount_unit)||0), 0);
      return { label:(g.recruiterName||"N/A").split(" ")[0].slice(0,9), value:Math.round(total*10)/10 };
    });

    return { statusMix, outcomes, followups, recruiterStacked, recruiterKeys, bubbleData, trend, recruiterHiredAmounts };
  }, [topStatuses, displaySummary, topRecruiters, followupSummary, subCompanies, rows]);

  /* ---- resume analytics ---- */
  const resumeAnalytics = useMemo(() => {
    const catMap = new Map();
    const ats = { "0–40":0,"41–60":0,"61–80":0,"81–100":0 };
    const exp = { "0–1y":0,"1–3y":0,"3–5y":0,"5y+":0 };

    resumes.forEach(r => {
      const cat=r.extracted_category||r.category||"Uncategorised";
      catMap.set(cat,(catMap.get(cat)||0)+1);
      const score=Number(r.ats_score||0);
      if (score<=40) ats["0–40"]++;
      else if (score<=60) ats["41–60"]++;
      else if (score<=80) ats["61–80"]++;
      else ats["81–100"]++;
      const yrs=Number(r.experience_years||0);
      if (yrs<=1) exp["0–1y"]++;
      else if (yrs<=3) exp["1–3y"]++;
      else if (yrs<=5) exp["3–5y"]++;
      else exp["5y+"]++;
    });

    const cats=[...catMap.entries()].sort((a,b)=>b[1]-a[1]);
    const category=cats.slice(0,5).map(([label,value],i)=>({ label,value,color:PIE_PALETTE[i%PIE_PALETTE.length] }));
    const rest=cats.slice(5).reduce((s,[,v])=>s+v,0);
    if (rest>0) category.push({ label:"Other",value:rest,color:"#94A3B8" });

    const atsColors=["#F43F5E","#F59E0B","#0EA5E9","#10B981"];
    const expColors=["#93C5FD","#60A5FA","#3B82F6","#1D4ED8"];
    return {
      total:resumes.length,
      category,
      ats:Object.entries(ats).map(([label,value],i)=>({ label,value,color:atsColors[i] })),
      experience:Object.entries(exp).map(([label,value],i)=>({ label,value,color:expColors[i] })),
    };
  }, [resumes]);

  const convRate = displaySummary.total ? Math.round((displaySummary.hired/displaySummary.total)*100) : 0;
  const toggleRecruiter = key => setExpandedRecruiters(p=>({ ...p,[key]:!p[key] }));

  const quickLinks = [
    { to:"/company/uploaded-resumes",        label:"Resume Library",          icon:"ti-files" },
    { to:"/company/recruiter-performance",   label:"Recruiter Performance",   icon:"ti-users" },
    { to:"/company/followup-report",         label:"Followup Report",         icon:"ti-clipboard-list" },
    { to:"/company/sub-companies",           label:"Sub Companies",           icon:"ti-building" },
    { to:"/company/requirements",            label:"Requirements",            icon:"ti-briefcase" },
  ];

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */
  return (
    <Layout
      title={isCompanyView ? "Dashboard" : "Resume Status Report"}
      description={
        isCompanyView
          ? "Company overview — pipeline, recruiter buckets, and quick access to the full workflow."
          : "Recruiter status rows, grouped and expandable."
      }
    >
      <div className="space-y-5">

        {/* ── STAT CARDS ───────────────────────────────────────────── */}
        <div style={{ display:"grid", gap:10, gridTemplateColumns: isCompanyView ? "repeat(6,1fr)" : "repeat(4,1fr)" }}>
          {isCompanyView ? (
            <>
              <StatCard label="Recruiters"     value={recruiterSummary.total_recruiters||0}        helper={`${recruiterSummary.active_recruiters||0} active`} accent="#6366F1" />
              <StatCard label="Total Rows"     value={displaySummary.total}                         helper={`${recruiterGroups.length} buckets`}              accent="#3B82F6" />
              <StatCard label="Active Pipeline" value={displaySummary.active}                       helper="In progress"                                      accent="#F59E0B" />
              <StatCard label="Hired"          value={displaySummary.hired}                         helper={`${convRate}% conversion`}                        accent="#10B981" />
              <StatCard label="Followups"      value={followupSummary.total||0}                     helper={`${followupSummary.pending||0} pending`}           accent="#0EA5E9" />
              <StatCard label="Hired Value"    value={formatAggregateHiredValue(recruiterSummary.total_hired_amount||0)} helper={`${displaySummary.hired} closed hires`} accent="#8B5CF6" />
            </>
          ) : (
            <>
              <StatCard label="Total Rows"     value={displaySummary.total}   helper="All tracked status rows"       accent="#6366F1" />
              <StatCard label="Active Pipeline" value={displaySummary.active}  helper="Still moving through hiring"   accent="#F59E0B" />
              <StatCard label="Hired"          value={displaySummary.hired}   helper="Converted into hires"          accent="#10B981" />
              <StatCard label="Closed"         value={displaySummary.closed}  helper="Closed without hiring"         accent="#F43F5E" />
            </>
          )}
        </div>

        {/* ── ANALYTICS SECTION ────────────────────────────────────── */}
        <div>
          <SectionHead
            title="Analytics"
            subtitle="A visual snapshot of your pipeline"
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-3" />
              </svg>
            }
          />

          {/* Row 1 — 3 cols: status donut | outcome pie | conversion rings */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
            <ChartCard title="Workflow Status Mix" subtitle="Current status distribution" compact>
              <Donut data={analytics.statusMix} size={130} thickness={16} centerSub="rows" />
            </ChartCard>
            <ChartCard title="Outcome Split" subtitle="Active vs hired vs closed" compact>
              <PieChart data={analytics.outcomes} size={130} />
            </ChartCard>
            <ChartCard title="Conversion Rates" subtitle="Key pipeline ratios" compact>
              <RingGroup
                total={displaySummary.total}
                hired={displaySummary.hired}
                active={displaySummary.active}
                closed={displaySummary.closed}
              />
            </ChartCard>
          </div>

          {/* Row 2 — 3 cols: funnel | recruiter stacked | followup bars */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1.5fr 1fr", gap:10, marginBottom:10 }}>
            <ChartCard title="Pipeline Funnel" subtitle="Stage-by-stage drop-off" compact>
              <PipelineFunnel
                total={displaySummary.total}
                active={displaySummary.active}
                hired={displaySummary.hired}
                closed={displaySummary.closed}
              />
            </ChartCard>
            <ChartCard title="Recruiter Breakdown" subtitle="Active · hired · closed per recruiter" compact>
              <StackedBar rows={analytics.recruiterStacked} keys={analytics.recruiterKeys} />
            </ChartCard>
            <ChartCard title="Followup Load" subtitle="Team follow-up tasks" compact>
              <HBars data={analytics.followups} />
            </ChartCard>
          </div>

          {/* Row 3 — 2 cols: workflow status bars | client bubbles */}
          <div style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr", gap:10, marginBottom:10 }}>
            <ChartCard title="Status Frequency" subtitle="Which statuses are most common" compact>
              <WorkflowStatusBars topStatuses={topStatuses} />
            </ChartCard>
            <ChartCard title="Client Requirement Load" subtitle="Open requirements per client" compact>
              <Bubbles data={analytics.bubbleData} height={160} />
            </ChartCard>
          </div>

          {/* Row 4 — full width: assignments trend + recruiter hired value */}
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10, marginBottom:10 }}>
            <ChartCard title="Assignments Over Time" subtitle="Rows added — last 6 months" compact>
              <AreaLine data={analytics.trend} color="#6366F1" height={130} />
            </ChartCard>
            <ChartCard title="Hired Value by Recruiter" subtitle="Total LPA placed" compact>
              <VBars
                data={analytics.recruiterHiredAmounts.length ? analytics.recruiterHiredAmounts : [{ label:"No data",value:0 }]}
                color="#8B5CF6"
                barAreaPx={100}
              />
            </ChartCard>
          </div>
        </div>

        {/* ── RESUME INSIGHTS SECTION ───────────────────────────────── */}
        <div>
          <SectionHead
            title="Resume Insights"
            subtitle={`Your resume library — ${resumeAnalytics.total} resumes`}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v10l8.5 5" /><circle cx="12" cy="12" r="10" />
              </svg>
            }
          />

          {/* Row 1 — 4 compact charts in a row */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
            <ChartCard title="By Category" subtitle={`${resumeAnalytics.total} total`} compact>
              <PieChart data={resumeAnalytics.category} size={120} />
            </ChartCard>
            <ChartCard title="ATS Score Bands" subtitle="How candidates score" compact>
              <PieChart data={resumeAnalytics.ats} size={120} />
            </ChartCard>
            <ChartCard title="Experience Spread" subtitle="Years in field" compact>
              <PieChart data={resumeAnalytics.experience} size={120} />
            </ChartCard>
            <ChartCard title="Category Distribution" subtitle="Bar view breakdown" compact>
              <VBars
                data={resumeAnalytics.category.slice(0,5).map(d=>({ label:d.label.slice(0,6), value:d.value, color:d.color }))}
                barAreaPx={105}
              />
            </ChartCard>
          </div>

          {/* Row 2 — ATS detailed + Experience detailed */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:10 }}>
            <ChartCard title="ATS Score — Horizontal" subtitle="Candidate scoring breakdown" compact>
              <HBars data={resumeAnalytics.ats} />
            </ChartCard>
            <ChartCard title="Experience — Horizontal" subtitle="Years of experience breakdown" compact>
              <HBars data={resumeAnalytics.experience} color="#6366F1" />
            </ChartCard>
          </div>
        </div>

        {/* ── COMPANY VIEW EXTRAS ───────────────────────────────────── */}
        {isCompanyView && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 0.9fr", gap:10 }}>

              {/* Project overview mini-grid */}
              <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECF4", padding:"16px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                <h3 style={{ margin:"0 0 12px", fontSize:13, fontWeight:700, color:"#1E293B" }}>Project Overview</h3>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {[
                    { label:"Resume Coverage",   value:recruiterSummary.total_assignments||displaySummary.total, color:"#EEF2FF",tc:"#4338CA" },
                    { label:"Conversion",        value:`${convRate}%`,  color:"#ECFDF5",tc:"#065F46" },
                    { label:"Followup Pressure", value:followupSummary.pending||0, color:"#FFFBEB",tc:"#92400E" },
                    { label:"Client Network",    value:subCompanies.length,        color:"#F0F9FF",tc:"#0C4A6E" },
                    { label:"Top Recruiters",    value:topRecruiters.length,       color:"#F5F3FF",tc:"#4C1D95" },
                    { label:"Max Req. Load",     value:[...subCompanies].sort((a,b)=>(b.requirements?.length||0)-(a.requirements?.length||0))[0]?.requirements?.length||0, color:"#FFF1F2",tc:"#881337" },
                  ].map(card => (
                    <div key={card.label} style={{ background:card.color, borderRadius:8, padding:"10px 12px" }}>
                      <div style={{ fontSize:9.5, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", color:card.tc, opacity:0.7 }}>{card.label}</div>
                      <div style={{ fontSize:20, fontWeight:800, color:card.tc, lineHeight:1.2, marginTop:2 }}>{card.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick access */}
              <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECF4", padding:"16px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                <h3 style={{ margin:"0 0 12px", fontSize:13, fontWeight:700, color:"#1E293B" }}>Quick Access</h3>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {quickLinks.map(link => (
                    <Link key={link.to} to={link.to}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, border:"1px solid #E8ECF4", textDecoration:"none", transition:"background 0.15s, border-color 0.15s" }}
                      onMouseEnter={e => { e.currentTarget.style.background="#EEF2FF"; e.currentTarget.style.borderColor="#C7D2FE"; }}
                      onMouseLeave={e => { e.currentTarget.style.background=""; e.currentTarget.style.borderColor="#E8ECF4"; }}
                    >
                      <i className={`ti ${link.icon}`} style={{ fontSize:15, color:"#6366F1", flexShrink:0 }} aria-hidden="true" />
                      <span style={{ fontSize:12, fontWeight:600, color:"#1E293B" }}>{link.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Workflow health */}
              <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECF4", padding:"16px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                <h3 style={{ margin:"0 0 4px", fontSize:13, fontWeight:700, color:"#1E293B" }}>Workflow Health</h3>
                <p style={{ margin:"0 0 12px", fontSize:10.5, color:"#94A3B8" }}>Statuses most common now</p>
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  {topStatuses.filter(s=>s.count>0).map(({ status, count }) => (
                    <div key={status} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#F8FAFC", borderRadius:7, padding:"5px 10px" }}>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusClasses[status]||"bg-slate-100 text-slate-700"}`}>
                        {status}
                      </span>
                      <span style={{ fontSize:13, fontWeight:700, color:"#1E293B" }}>{count}</span>
                    </div>
                  ))}
                  {!topStatuses.some(s=>s.count>0) && (
                    <div style={{ padding:"16px 10px", textAlign:"center", fontSize:12, color:"#94A3B8" }}>No statuses yet.</div>
                  )}
                </div>
              </div>
            </div>

            {/* Top recruiters + requirement coverage */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECF4", padding:"16px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                  <div>
                    <h3 style={{ margin:0, fontSize:13, fontWeight:700, color:"#1E293B" }}>Top Recruiters</h3>
                    <p style={{ margin:"2px 0 0", fontSize:10.5, color:"#94A3B8" }}>Busiest recruiter buckets by volume</p>
                  </div>
                  <Link to="/company/recruiter-performance"
                    style={{ fontSize:11, fontWeight:600, color:"#6366F1", textDecoration:"none", padding:"4px 10px", borderRadius:6, border:"1px solid #E0E7FF", background:"#F5F3FF" }}>
                    View All
                  </Link>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {topRecruiters.length ? topRecruiters.map(g => (
                    <div key={g.recruiterKey} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", borderRadius:8, border:"1px solid #E8ECF4", padding:"8px 12px" }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#1E293B" }}>{g.recruiterName}</div>
                        <div style={{ fontSize:10, color:"#94A3B8", marginTop:2 }}>
                          <span style={{ color:"#F59E0B",fontWeight:600 }}>{g.active}</span> active &nbsp;·&nbsp;
                          <span style={{ color:"#10B981",fontWeight:600 }}>{g.hired}</span> hired &nbsp;·&nbsp;
                          <span style={{ color:"#F43F5E",fontWeight:600 }}>{g.closed}</span> closed
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:18, fontWeight:800, color:"#1E293B" }}>{g.total}</div>
                        <div style={{ fontSize:10, color:"#94A3B8" }}>rows</div>
                      </div>
                    </div>
                  )) : (
                    <div style={{ padding:"20px", textAlign:"center", fontSize:12, color:"#94A3B8" }}>No recruiter activity yet.</div>
                  )}
                </div>
              </div>

              <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECF4", padding:"16px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                  <div>
                    <h3 style={{ margin:0, fontSize:13, fontWeight:700, color:"#1E293B" }}>Requirement Coverage</h3>
                    <p style={{ margin:"2px 0 0", fontSize:10.5, color:"#94A3B8" }}>Active requirements per client</p>
                  </div>
                  <Link to="/company/sub-companies"
                    style={{ fontSize:11, fontWeight:600, color:"#6366F1", textDecoration:"none", padding:"4px 10px", borderRadius:6, border:"1px solid #E0E7FF", background:"#F5F3FF" }}>
                    View All
                  </Link>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {subCompanies.length ? [...subCompanies]
                    .sort((a,b)=>(b.requirements?.length||0)-(a.requirements?.length||0)||a.name.localeCompare(b.name))
                    .slice(0,5)
                    .map(company => (
                    <div key={company.id} style={{ borderRadius:8, border:"1px solid #E8ECF4", padding:"8px 12px" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <div style={{ fontSize:12, fontWeight:600, color:"#1E293B" }}>{company.name}</div>
                        <span style={{ fontSize:10, fontWeight:700, color:"#6366F1", background:"#EEF2FF", padding:"2px 7px", borderRadius:99 }}>
                          {company.requirements?.length||0} req
                        </span>
                      </div>
                      <div style={{ fontSize:10, color:"#94A3B8", marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {(company.requirements||[]).slice(0,2).map(r=>r.name).join(", ")||"No requirements yet"}
                      </div>
                    </div>
                  )) : (
                    <div style={{ padding:"20px", textAlign:"center", fontSize:12, color:"#94A3B8" }}>No sub companies yet.</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── RECRUITER STATUS TABLE ────────────────────────────────── */}
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECF4", overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:10, borderBottom:"1px solid #E8ECF4", padding:"14px 16px" }}
            className="md:flex-row md:items-center md:justify-between">
            <div>
              <h3 style={{ margin:0, fontSize:13, fontWeight:700, color:"#1E293B" }}>
                {isCompanyView ? "Recruiter Status Tracking" : "Status Tracking"}
              </h3>
              <p style={{ margin:"3px 0 0", fontSize:10.5, color:"#94A3B8" }}>
                Recruiters stay collapsed by default — expand to see row-level detail.
              </p>
            </div>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search candidate, recruiter, company..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs md:max-w-xs"
            />
          </div>

          <div className="overflow-x-auto">
            <table style={{ width:"100%", fontSize:11.5, borderCollapse:"collapse" }}>
              <thead style={{ background:"#F8FAFC", borderBottom:"1px solid #E8ECF4" }}>
                <tr>
                  {["Recruiter","Total","Active","Hired","Closed","Updated"].map(h => (
                    <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontWeight:600, color:"#64748B", fontSize:10.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recruiterGroups.map(group => {
                  const isExpanded = !!expandedRecruiters[group.recruiterKey];
                  return (
                    <React.Fragment key={group.recruiterKey}>
                      <tr style={{ borderBottom:"1px solid #F1F5F9", cursor:"pointer", transition:"background 0.1s" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#FAFBFD"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}
                      >
                        <td style={{ padding:"10px 12px" }}>
                          <button type="button" onClick={() => toggleRecruiter(group.recruiterKey)}
                            style={{ display:"flex", alignItems:"center", gap:8, background:"none", border:"none", cursor:"pointer", padding:0 }}>
                            <span style={{
                              width:22, height:22, borderRadius:"50%",
                              border:"1px solid #E8ECF4", background:"#fff",
                              display:"grid", placeItems:"center", color:"#64748B",
                            }}>
                              <svg viewBox="0 0 20 20" style={{ width:12, height:12, transform:isExpanded?"rotate(180deg)":"", transition:"transform 0.2s" }} fill="none">
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                            <span>
                              <div style={{ fontWeight:600, color:"#1E293B", fontSize:12 }}>{group.recruiterName}</div>
                              <div style={{ fontSize:10, color:"#94A3B8", marginTop:1 }}>{group.recruiterMeta}</div>
                            </span>
                          </button>
                        </td>
                        <td style={{ padding:"10px 12px", fontWeight:700, color:"#1E293B" }}>{group.total}</td>
                        <td style={{ padding:"10px 12px", fontWeight:700, color:"#B45309" }}>{group.active}</td>
                        <td style={{ padding:"10px 12px", fontWeight:700, color:"#065F46" }}>{group.hired}</td>
                        <td style={{ padding:"10px 12px", fontWeight:700, color:"#9F1239" }}>{group.closed}</td>
                        <td style={{ padding:"10px 12px", color:"#94A3B8" }}>{formatDate(group.latestUpdatedAt)}</td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={6} style={{ padding:"0 0 0 32px", background:"#FAFBFD" }}>
                            <div style={{ margin:"10px 12px 10px 0", borderRadius:10, border:"1px solid #E8ECF4", overflow:"hidden", background:"#fff" }}>
                              <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse" }}>
                                <thead style={{ background:"#F8FAFC", borderBottom:"1px solid #E8ECF4" }}>
                                  <tr>
                                    {["Record","Candidate","Company","Requirement","Status","ATS","Updated"].map(h => (
                                      <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontWeight:600, color:"#64748B", fontSize:10 }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.rows.map(row => (
                                    <tr key={row.id}
                                      style={{
                                        borderBottom:"1px solid #F1F5F9",
                                        background:row.is_hired?"rgba(16,185,129,0.04)":row.is_locked?"rgba(244,63,94,0.04)":"",
                                      }}>
                                      <td style={{ padding:"8px 10px" }}>
                                        <div style={{ fontWeight:600, color:"#1E293B" }}>#{row.id}</div>
                                        <div style={{ fontSize:9.5, color:"#94A3B8", marginTop:1 }}>{row.original_filename}</div>
                                      </td>
                                      <td style={{ padding:"8px 10px" }}>
                                        <div style={{ fontWeight:600, color:"#1E293B" }}>{row.candidate_name||"Candidate"}</div>
                                        <div style={{ fontSize:9.5, color:"#94A3B8" }}>{row.candidate_role||"—"}</div>
                                      </td>
                                      <td style={{ padding:"8px 10px", color:"#475569" }}>
                                        <div>{row.sub_company_name||"—"}</div>
                                        {row.is_hired && row.hired_company_name && (
                                          <div style={{ fontSize:9.5, color:"#10B981", marginTop:1 }}>→ {row.hired_company_name}</div>
                                        )}
                                      </td>
                                      <td style={{ padding:"8px 10px", color:"#475569" }}>
                                        <div>{row.requirement_name||"—"}</div>
                                        {row.is_hired && row.hired_requirement_name && (
                                          <div style={{ fontSize:9.5, color:"#10B981", marginTop:1 }}>{row.hired_requirement_name}</div>
                                        )}
                                      </td>
                                      <td style={{ padding:"8px 10px" }}>
                                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${statusClasses[row.lead_status]||"bg-slate-100 text-slate-700"}`}>
                                          {row.lead_status}
                                        </span>
                                        {row.hired_amount && (
                                          <div style={{ fontSize:9.5, color:"#10B981", marginTop:3 }}>
                                            {formatHiredAmount(row.hired_amount,row.hired_amount_unit)}
                                          </div>
                                        )}
                                      </td>
                                      <td style={{ padding:"8px 10px" }}>
                                        <span style={{
                                          display:"inline-block",
                                          fontSize:10, fontWeight:700,
                                          color: Number(row.ats_score||0)>=80?"#065F46":Number(row.ats_score||0)>=60?"#1E40AF":"#9F1239",
                                          background: Number(row.ats_score||0)>=80?"#ECFDF5":Number(row.ats_score||0)>=60?"#EFF6FF":"#FFF1F2",
                                          padding:"2px 6px", borderRadius:99,
                                        }}>
                                          {row.ats_score||0}%
                                        </span>
                                      </td>
                                      <td style={{ padding:"8px 10px", color:"#94A3B8" }}>{formatDate(row.updated_at||row.assigned_at)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {recruiterGroups.length===0 && (
                  <tr>
                    <td colSpan={6} style={{ padding:"32px", textAlign:"center", color:"#94A3B8", fontSize:12 }}>
                      No resume status rows found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </Layout>
  );
}